const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_API_CODES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const DEFAULT_MESSAGES = {
  WELCOME: "Hi there! Thanks for reaching out. How can we help you today?",
  AWAY: "Hello! We're currently away. We'll get back to you as soon as we're back online."
};

function defaultTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

function defaultWeekdaySchedule(startTime = "09:00", endTime = "18:00") {
  return ["MON", "TUE", "WED", "THU", "FRI"].map((day) => ({
    day,
    startTime,
    endTime
  }));
}

export function createDefaultRules() {
  return [
    {
      id: "welcome_1",
      type: "WELCOME",
      enabled: false,
      config: {
        message: { type: "text", text: DEFAULT_MESSAGES.WELCOME }
      }
    },
    {
      id: "away_1",
      type: "AWAY",
      enabled: false,
      config: {
        timezone: defaultTimezone(),
        schedule: defaultWeekdaySchedule(),
        message: { type: "text", text: DEFAULT_MESSAGES.AWAY }
      }
    }
  ];
}

function findRuleByType(rules, type) {
  return (rules || []).find((rule) => rule.type === type);
}

export function hasSavedBasicAutomations(apiRules) {
  return Array.isArray(apiRules) && apiRules.length > 0;
}

/** Merge API rules with defaults so WELCOME and AWAY are always present. */
export function normalizeBasicAutomationRules(apiRules) {
  const defaults = createDefaultRules();
  if (!hasSavedBasicAutomations(apiRules)) {
    return defaults;
  }

  return defaults.map((defaultRule) => {
    const fromApi = findRuleByType(apiRules, defaultRule.type);
    if (!fromApi) return defaultRule;

    return {
      ...defaultRule,
      ...fromApi,
      id: fromApi.id || defaultRule.id,
      config: {
        ...defaultRule.config,
        ...fromApi.config,
        message: {
          ...defaultRule.config.message,
          ...fromApi.config?.message
        }
      }
    };
  });
}

export function syncWorkingHoursState(rules, setWorkingDays, setStartTime, setEndTime) {
  const awayRule = rules.find((rule) => rule.type === "AWAY");
  if (!awayRule?.config?.schedule) return;
  const hours = getWorkingHoursFromSchedule(awayRule.config.schedule);
  setWorkingDays(hours.days);
  setStartTime(hours.startTime);
  setEndTime(hours.endTime);
}

export function getRuleMessageText(rule) {
  return rule?.config?.message?.text ?? "";
}

export function setRuleMessageText(rule, text) {
  return {
    ...rule,
    config: {
      ...rule.config,
      message: { type: "text", text }
    }
  };
}

export function apiDayToLabel(apiDay) {
  const index = DAY_API_CODES.indexOf(apiDay);
  return index >= 0 ? DAY_LABELS[index] : apiDay;
}

export function labelToApiDay(label) {
  const index = DAY_LABELS.indexOf(label);
  return index >= 0 ? DAY_API_CODES[index] : label;
}

/** Extract working-hours UI state from an AWAY rule schedule. */
export function getWorkingHoursFromSchedule(schedule) {
  const entries = Array.isArray(schedule) ? schedule : [];
  const days = entries.map((entry) => apiDayToLabel(entry.day));
  const first = entries[0];
  return {
    days: days.length ? days : ["Mon", "Tue", "Wed", "Thu", "Fri"],
    startTime: first?.startTime || "09:00",
    endTime: first?.endTime || "18:00"
  };
}

export function buildScheduleFromWorkingHours({ days, startTime, endTime }) {
  return (days || []).map((day) => ({
    day: labelToApiDay(day),
    startTime,
    endTime
  }));
}

function serializeWelcomeRule(rule) {
  return {
    id: rule.id,
    type: "WELCOME",
    enabled: rule.enabled,
    config: {
      message: { type: "text", text: rule.config?.message?.text ?? "" }
    }
  };
}

function serializeAwayRule(rule) {
  return {
    id: rule.id,
    type: "AWAY",
    enabled: rule.enabled,
    config: {
      timezone: rule.config.timezone,
      schedule: rule.config.schedule,
      message: { type: "text", text: rule.config?.message?.text ?? "" }
    }
  };
}

export function buildSaveBasicAutomationsPayload(rules) {
  const serialized = [];
  const welcome = findRuleByType(rules, "WELCOME");
  const away = findRuleByType(rules, "AWAY");
  if (welcome) serialized.push(serializeWelcomeRule(welcome));
  if (away) serialized.push(serializeAwayRule(away));

  return {
    operation: "SAVE_BASIC_AUTOMATIONS",
    payload: { rules: serialized }
  };
}

export const BASIC_AUTOMATION_META = [
  {
    type: "WELCOME",
    title: "Welcome Message",
    description:
      "WHEN a new customer messages for the first time (or returns after 24h) → send automatically"
  },
  {
    type: "AWAY",
    title: "Away / Out-of-Office",
    description: "WHEN a customer messages outside your working hours → send automatically"
  }
];

export { DAY_LABELS };
